import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  auctionApi as generatedAuctionApi,
  GetVehicleAuctionApiResponse,
} from './codegen/__generated__/api.generated';

export type {
  DeclineOfferRequestBody,
  PublishAuctionRequestBody,
} from './codegen/__generated__/api.generated';

type VehicleArg = {vehicleId: string};

type AuctionMutationLifecycle = {
  dispatch: (action: ReturnType<typeof generatedAuctionApi.util.upsertQueryData>) => unknown;
  queryFulfilled: Promise<{data: GetVehicleAuctionApiResponse}>;
};

const vehicleActivityTag = (vehicleId: string) => ({
  type: 'VehicleActivity' as const,
  id: vehicleId,
});

// Each auction action returns the new view: write it into the getVehicleAuction cache.
// Each auction action also writes a sales activity: refetch the auction history.
const storeReturnedView = {
  async onQueryStarted(arg: VehicleArg, {dispatch, queryFulfilled}: AuctionMutationLifecycle) {
    const result = await queryFulfilled.catch(() => undefined);
    if (result !== undefined) {
      dispatch(
        generatedAuctionApi.util.upsertQueryData(
          'getVehicleAuction',
          {vehicleId: arg.vehicleId},
          result.data
        )
      );
    }
  },
  invalidatesTags: invalidateOnSuccess((_result, arg: VehicleArg) => [
    vehicleActivityTag(arg.vehicleId),
  ]),
};

// The BE opens a sale business case for the vehicle: refetch the business case data of the vehicle.
const opensSaleBusinessCase = {
  ...storeReturnedView,
  invalidatesTags: invalidateOnSuccess((_result, arg: VehicleArg) => [
    vehicleActivityTag(arg.vehicleId),
    {type: 'VehicleLastActiveBC' as const, id: arg.vehicleId},
    {type: 'BusinessCaseCountByVehicle' as const, id: arg.vehicleId},
  ]),
};

export const auctionApi = generatedAuctionApi.enhanceEndpoints({
  endpoints: {
    getVehicleAuction: {
      // A poll can show trader offers or a clock transition: refetch the auction history.
      async onQueryStarted(arg, {dispatch, queryFulfilled, getCacheEntry}) {
        const isRefetch = getCacheEntry().data !== undefined;
        const result = await queryFulfilled.catch(() => undefined);
        if (isRefetch && result !== undefined) {
          dispatch(omneticApi.util.invalidateTags([vehicleActivityTag(arg.vehicleId)]));
        }
      },
    },
    publishVehicleAuction: storeReturnedView,
    confirmVehicleAuctionWinner: opensSaleBusinessCase,
    declineVehicleAuctionOffer: storeReturnedView,
    endVehicleAuctionBidding: storeReturnedView,
    relaunchVehicleAuction: storeReturnedView,
    abandonVehicleAuction: storeReturnedView,
    keepVehicleAuctionInStock: storeReturnedView,
    requestVehicleAuctionSaleBusinessCase: opensSaleBusinessCase,
  },
});

export const {
  useGetVehicleAuctionQuery,
  usePublishVehicleAuctionMutation,
  useConfirmVehicleAuctionWinnerMutation,
  useDeclineVehicleAuctionOfferMutation,
  useEndVehicleAuctionBiddingMutation,
  useRelaunchVehicleAuctionMutation,
  useAbandonVehicleAuctionMutation,
  useKeepVehicleAuctionInStockMutation,
  useRequestVehicleAuctionSaleBusinessCaseMutation,
} = auctionApi;
